import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr, num } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronRight, Home, RefreshCw, FileSearch, Filter } from "lucide-react";

const LEVEL_LABEL = { opco: "Operating Company", region: "Region", rso: "RSO", ao: "Area Office", dealer: "Dealer" };

export default function CalcCentre() {
  const [path, setPath] = useState([{ level: "opco", parent_id: null, label: "All OpCos" }]);
  const current = path[path.length - 1];
  const [detail, setDetail] = useState(null);
  const [flt, setFlt] = useState({ from_date: "", to_date: "", scheme_id: "" });

  const { data: schemeOpts } = useQuery({ queryKey: ["calc-schemes"], queryFn: async () => (await api.get("/schemes")).data });
  useEffect(() => { setPath([{ level: "opco", parent_id: null, label: "All OpCos" }]); }, [flt]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["calc-hier", current.level, current.parent_id, flt],
    queryFn: async () =>
      (await api.get("/calc/hierarchy", { params: {
        level: current.level, parent_id: current.parent_id,
        ...(flt.from_date ? { from_date: flt.from_date } : {}),
        ...(flt.to_date ? { to_date: flt.to_date } : {}),
        ...(flt.scheme_id ? { scheme_id: flt.scheme_id } : {}),
      } })).data,
  });

  const drill = (row) => {
    if (!data?.next || data.next === "ledger") {
      openLedger(row.ledger_id);
      return;
    }
    setPath((p) => [...p, { level: data.next, parent_id: row.id, label: row.name }]);
  };

  const openLedger = async (ledgerId) => {
    if (!ledgerId) return;
    try {
      const d = (await api.get(`/calc/ledger/${ledgerId}`)).data;
      setDetail(d);
    } catch (e) { toast.error("Could not load ledger entry"); }
  };

  const goTo = (idx) => setPath((p) => p.slice(0, idx + 1));

  return (
    <div>
      <PageHeader title="Calculation Control Centre"
        subtitle="Drill down Company → OpCo → Region → RSO → AO → Dealer → Ledger"
        actions={
          <Button data-testid="calc-refresh-btn" variant="outline" onClick={() => refetch()} className="gap-2">
            <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} /> Refresh
          </Button>
        } />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="calc-filter">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mr-2"><Filter size={15} /> Filters</div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">From Date</div>
          <Input type="date" className="h-9 w-40" data-testid="calc-filter-from" value={flt.from_date} onChange={(e) => setFlt((f) => ({ ...f, from_date: e.target.value }))} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">To Date</div>
          <Input type="date" className="h-9 w-40" data-testid="calc-filter-to" value={flt.to_date} onChange={(e) => setFlt((f) => ({ ...f, to_date: e.target.value }))} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Scheme</div>
          <select className="h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm" data-testid="calc-filter-scheme"
            value={flt.scheme_id} onChange={(e) => setFlt((f) => ({ ...f, scheme_id: e.target.value }))}>
            <option value="">All Schemes</option>
            {(schemeOpts?.schemes || []).map((s) => <option key={s.id} value={s.id}>{s.code}</option>)}
          </select>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 flex-wrap mb-4 text-sm" data-testid="calc-breadcrumb">
        {path.map((p, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <button onClick={() => goTo(i)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md ${i === path.length - 1 ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {i === 0 && <Home size={13} />} {p.label}
            </button>
            {i < path.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </span>
        ))}
      </div>

      <Panel testid="calc-table-panel" title={`${LEVEL_LABEL[current.level] || current.level} breakdown`}
        subtitle={data ? `${data.rows.length} rows · click a row to drill deeper` : ""}>
        {isLoading || !data ? <Loading /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                  <th className="py-2.5 pr-3">{LEVEL_LABEL[current.level]}</th>
                  {current.level === "dealer" && <th className="py-2.5 pr-3">Scheme</th>}
                  {current.level === "dealer" && <th className="py-2.5 pr-3">Engine</th>}
                  {current.level !== "dealer" && <th className="py-2.5 pr-3 text-right">Dealers</th>}
                  <th className="py-2.5 pr-3 text-right">Volume (MT)</th>
                  <th className="py-2.5 pr-3 text-right">Accrued</th>
                  <th className="py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.id || i} onClick={() => drill(r)} data-testid={`calc-row-${i}`}
                    className="drill-row border-b border-slate-50 cursor-pointer">
                    <td className="py-3 pr-3 font-medium text-slate-800">
                      {r.name} {r.code && <span className="text-xs text-slate-400 font-mono">({r.code})</span>}
                    </td>
                    {current.level === "dealer" && <td className="py-3 pr-3 text-slate-600 font-mono text-xs">{r.scheme}</td>}
                    {current.level === "dealer" && <td className="py-3 pr-3"><Pill>{r.engine}</Pill></td>}
                    {current.level !== "dealer" && <td className="py-3 pr-3 text-right text-slate-600">{num(r.dealer_count)}</td>}
                    <td className="py-3 pr-3 text-right text-slate-600">{num(r.qty)}</td>
                    <td className="py-3 pr-3 text-right font-semibold text-[#00529B]">{inr(r.accrued)}</td>
                    <td className="py-3 text-right text-slate-300">
                      {current.level === "dealer" ? <FileSearch size={15} /> : <ChevronRight size={15} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Ledger detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="ledger-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Immutable Ledger Entry</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dealer" value={`${detail.dealer_name} (${detail.dealer_code})`} />
                <Field label="Scheme" value={`${detail.scheme_code} v${detail.scheme_version}`} />
                <Field label="Engine" value={detail.engine} />
                <Field label="Run ID" value={detail.run_id?.slice(0, 12)} mono />
                <Field label="Eligibility" value={detail.eligibility} />
                <Field label="Actual Qty" value={`${num(detail.actual_qty_mt)} MT`} />
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Calculation Result</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(detail.result || {}).map(([kk, vv]) => (
                    <div key={kk} className="flex justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                      <span className="text-slate-500">{kk}</span>
                      <span className="font-medium text-slate-800 text-right">{typeof vv === "object" ? JSON.stringify(vv) : String(vv)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center rounded-lg bg-[#0F2C59] text-white p-4">
                <span className="text-sm text-white/70">Final Incentive Amount</span>
                <span className="font-heading text-2xl font-bold">{inr(detail.final_amount)}</span>
              </div>
              <div className="text-xs text-slate-400">{detail.eligibility_reason}</div>
              {detail.invoice_lines?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Sample Invoice Lines</div>
                  <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-xs">
                      <tbody>
                        {detail.invoice_lines.slice(0, 10).map((l) => (
                          <tr key={l.id} className="border-b border-slate-50">
                            <td className="py-1.5 px-2 font-mono text-slate-500">{l.invoice_no}</td>
                            <td className="py-1.5 px-2 text-right">{num(l.qty_mt)} MT</td>
                            <td className="py-1.5 px-2 text-right text-slate-500">{inr(l.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`text-slate-800 font-medium ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}
